import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  LinearProgress,
} from "@mui/material";
import {
  Gavel as GavelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TextSnippet as TextSnippetIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { jsPDF } from "jspdf";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import AppService from "../services/AppService";
import LegalService from "../services/LegalService";
import AIService from "../services/AIService";
import { App } from "../types/app";
import { LegalDocument, CreateLegalDocDTO } from "../types/legal";

const LEGAL_TEXT_MODELS = [
  "gemini-2.5-flash-lite",
  "gemini-2.5-pro",
  "gpt-4.1-mini",
  "gpt-4.1",
  "o4-mini",
];
const DEFAULT_CONTACT_EMAIL = "violesoftware@gmail.com";
const COMMON_PERSONAL_INFO_ITEMS = [
  "Name",
  "Email address",
  "Phone number",
  "Device identifiers",
  "IP address",
  "Location data",
  "Payment information",
  "Usage analytics",
];
const DEFAULT_PERSONAL_INFO_ITEMS = [
  "Name",
  "Email address",
  "Device identifiers",
  "Usage analytics",
];
const PUBLIC_WEB_APP_ORIGIN = "https://translatorx-5774e.web.app";
const GENERATABLE_DOC_TYPES: Array<LegalDocument["type"]> = [
  "privacy",
  "tos",
  "eula",
  "cookie",
  "dpa",
];

const Legal: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [mainTab, setMainTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<LegalDocument | null>(null);
  const [viewDocumentOpen, setViewDocumentOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<LegalDocument | null>(
    null,
  );
  const [editDocumentOpen, setEditDocumentOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<LegalDocument | null>(
    null,
  );
  const [editContent, setEditContent] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [updatingLiveDocumentId, setUpdatingLiveDocumentId] = useState("");
  const [selectedGenerationTypes, setSelectedGenerationTypes] = useState<
    Array<LegalDocument["type"]>
  >(["privacy", "tos"]);
  const [generationFlowState, setGenerationFlowState] = useState<
    "idle" | "in_progress" | "success"
  >("idle");
  const [generationProgress, setGenerationProgress] = useState({
    total: 0,
    completed: 0,
    currentType: "",
  });
  const [lastGeneratedTypes, setLastGeneratedTypes] = useState<string[]>([]);
  const [copiedLinkDocumentId, setCopiedLinkDocumentId] = useState("");
  const [viewTab, setViewTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState<CreateLegalDocDTO>({
    appId: "",
    type: "privacy",
    generationModel: AIService.getDefaultModel(),
    companyName: "",
    companyAddress: "",
    contactEmail: DEFAULT_CONTACT_EMAIL,
    website: "",
    jurisdiction: "US",
    appType: "mobile",
    dataCollection: {
      personalInfo: DEFAULT_PERSONAL_INFO_ITEMS,
      thirdPartyServices: [],
      cookies: false,
      analytics: false,
      advertising: false,
    },
    features: {
      userAccounts: true,
      inAppPurchases: true,
      userGeneratedContent: true,
      location: true,
      pushNotifications: true,
    },
  });
  const [personalInfoInput, setPersonalInfoInput] = useState("");
  const [thirdPartyInput, setThirdPartyInput] = useState("");

  useEffect(() => {
    if (user) {
      loadApps();
    }
  }, [user]);

  useEffect(() => {
    if (selectedApp && user) {
      loadDocuments();
    }
  }, [selectedApp, user]);

  useEffect(() => {
    if (!selectedApp) return;
    setFormData((current) => ({
      ...current,
      appId: selectedApp.appId,
      companyName: selectedApp.name,
      contactEmail: DEFAULT_CONTACT_EMAIL,
      features: {
        userAccounts: true,
        inAppPurchases: true,
        userGeneratedContent: true,
        location: true,
        pushNotifications: true,
      },
      dataCollection: {
        ...current.dataCollection,
        personalInfo:
          current.dataCollection.personalInfo.length > 0
            ? current.dataCollection.personalInfo
            : DEFAULT_PERSONAL_INFO_ITEMS,
      },
    }));
  }, [selectedApp]);

  const loadApps = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (err) {
      setError("Failed to load apps");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!user || !selectedApp) return;

    try {
      setLoading(true);
      const docs = await LegalService.getDocumentsByAppId(
        selectedApp.appId,
        user.email,
      );
      setDocuments(docs);
    } catch (err) {
      setError("Failed to load documents");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (selectedApp) {
      setFormData((current) => ({
        ...current,
        appId: selectedApp.appId,
        companyName: selectedApp.name,
        contactEmail: DEFAULT_CONTACT_EMAIL,
        features: {
          userAccounts: true,
          inAppPurchases: true,
          userGeneratedContent: true,
          location: true,
          pushNotifications: true,
        },
        dataCollection: {
          ...current.dataCollection,
          personalInfo:
            current.dataCollection.personalInfo.length > 0
              ? current.dataCollection.personalInfo
              : DEFAULT_PERSONAL_INFO_ITEMS,
        },
      }));
    }
    if (!selectedGenerationTypes.length) {
      setSelectedGenerationTypes(["privacy", "tos"]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPersonalInfoInput("");
    setThirdPartyInput("");
  };

  const getLegalTypeLabel = (type: LegalDocument["type"]): string =>
    LegalService.getDocumentTypeName(type);

  const toggleGenerationType = (type: LegalDocument["type"]) => {
    setSelectedGenerationTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type],
    );
  };

  const handleGenerate = async () => {
    if (!user || !selectedApp) return;
    if (!selectedGenerationTypes.length) {
      setError("Select at least one legal document type to generate.");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setOpenDialog(false);
      setGenerationFlowState("in_progress");
      setGenerationProgress({
        total: selectedGenerationTypes.length,
        completed: 0,
        currentType: "",
      });

      for (let index = 0; index < selectedGenerationTypes.length; index += 1) {
        const docType = selectedGenerationTypes[index];
        setGenerationProgress({
          total: selectedGenerationTypes.length,
          completed: index,
          currentType: getLegalTypeLabel(docType),
        });

        await LegalService.generateDocument(
          user.email,
          {
            ...formData,
            type: docType,
          },
          selectedApp.name,
          selectedApp.description,
          [
            selectedApp.appId,
            selectedApp.googlePlayPackageName || "",
            selectedApp.storeId || "",
          ].filter((value) => Boolean(String(value).trim())),
        );
      }

      const publicAppParam = encodeURIComponent(selectedApp.appId);
      await AppService.updateApp(selectedApp.appId, user.email, {
        privacyPolicyUrl: `${PUBLIC_WEB_APP_ORIGIN}/privacy?app=${publicAppParam}`,
        termsOfServiceUrl: `${PUBLIC_WEB_APP_ORIGIN}/terms?app=${publicAppParam}`,
      });

      await loadDocuments();
      setGenerationProgress({
        total: selectedGenerationTypes.length,
        completed: selectedGenerationTypes.length,
        currentType: "",
      });
      setLastGeneratedTypes(
        selectedGenerationTypes.map((type) => getLegalTypeLabel(type)),
      );
      setGenerationFlowState("success");
      toast.success(
        `Generated ${selectedGenerationTypes.length} legal document${
          selectedGenerationTypes.length === 1 ? "" : "s"
        } successfully.`,
      );
    } catch (err) {
      setGenerationFlowState("idle");
      setError("Failed to generate document");
      console.error(err);
      toast.error("Failed to generate legal documents.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteClick = (doc: LegalDocument) => {
    setDocToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!user || !docToDelete) return;

    try {
      setLoading(true);
      await LegalService.deleteDocument(docToDelete.documentId, user.email);
      await loadDocuments();
      setDeleteConfirmOpen(false);
      setDocToDelete(null);
    } catch (err) {
      setError("Failed to delete document");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (doc: LegalDocument) => {
    setViewingDocument(doc);
    setViewDocumentOpen(true);
    setViewTab(0);
  };

  const handleDownloadPlainText = (doc: LegalDocument) => {
    const blob = new Blob([doc.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeAppName = (selectedApp?.name || doc.appId).replace(
      /[^a-zA-Z0-9-_]/g,
      "_",
    );
    a.download = `${safeAppName}-${doc.type}-v${doc.versionNumber || doc.version}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = (doc: LegalDocument) => {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });
    const margin = 40;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const safeAppName = (selectedApp?.name || doc.appId).replace(
      /[^a-zA-Z0-9-_]/g,
      "_",
    );

    const title = `${LegalService.getDocumentTypeName(doc.type)} (v${
      doc.versionNumber || doc.version
    })`;
    pdf.setFontSize(16);
    pdf.text(title, margin, margin);
    pdf.setFontSize(10);
    pdf.text(`App: ${selectedApp?.name || doc.appId}`, margin, margin + 18);
    pdf.text(
      `Last updated: ${doc.lastUpdated.toLocaleDateString()}`,
      margin,
      margin + 32,
    );

    pdf.setFontSize(11);
    const lines = pdf.splitTextToSize(doc.content, contentWidth);
    let y = margin + 56;
    const lineHeight = 14;
    lines.forEach((line: string) => {
      if (y > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += lineHeight;
    });

    pdf.save(
      `${safeAppName}-${doc.type}-v${doc.versionNumber || doc.version}.pdf`,
    );
  };

  const addPersonalInfo = () => {
    if (personalInfoInput.trim()) {
      setFormData({
        ...formData,
        dataCollection: {
          ...formData.dataCollection,
          personalInfo: [
            ...formData.dataCollection.personalInfo,
            personalInfoInput.trim(),
          ],
        },
      });
      setPersonalInfoInput("");
    }
  };

  const removePersonalInfo = (index: number) => {
    setFormData({
      ...formData,
      dataCollection: {
        ...formData.dataCollection,
        personalInfo: formData.dataCollection.personalInfo.filter(
          (_, i) => i !== index,
        ),
      },
    });
  };

  const addThirdParty = () => {
    if (thirdPartyInput.trim()) {
      setFormData({
        ...formData,
        dataCollection: {
          ...formData.dataCollection,
          thirdPartyServices: [
            ...formData.dataCollection.thirdPartyServices,
            thirdPartyInput.trim(),
          ],
        },
      });
      setThirdPartyInput("");
    }
  };

  const removeThirdParty = (index: number) => {
    setFormData({
      ...formData,
      dataCollection: {
        ...formData.dataCollection,
        thirdPartyServices: formData.dataCollection.thirdPartyServices.filter(
          (_, i) => i !== index,
        ),
      },
    });
  };

  const managedDocuments = documents;
  const groupedDocumentsByDate = useMemo(() => {
    const grouped = new Map<string, LegalDocument[]>();
    managedDocuments.forEach((doc) => {
      const key = doc.lastUpdated.toISOString().slice(0, 10);
      const existing = grouped.get(key) || [];
      existing.push(doc);
      grouped.set(key, existing);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dateKey, docs]) => ({
        dateKey,
        label: new Date(`${dateKey}T00:00:00`).toLocaleDateString(),
        docs: docs.sort(
          (a, b) =>
            (b.versionNumber || 1) - (a.versionNumber || 1) ||
            b.lastUpdated.getTime() - a.lastUpdated.getTime(),
        ),
      }));
  }, [managedDocuments]);

  const handleEditDocument = (doc: LegalDocument) => {
    setEditingDocument(doc);
    setEditContent(doc.content);
    setEditDocumentOpen(true);
  };

  const handleSaveEditedDocument = async () => {
    if (!user || !editingDocument) return;
    const trimmedContent = editContent.trim();
    if (!trimmedContent) {
      setError("Document content cannot be empty");
      return;
    }

    try {
      setSavingEdit(true);
      await LegalService.updateDocumentContent(
        editingDocument.documentId,
        user.email,
        trimmedContent,
      );
      await loadDocuments();
      setEditDocumentOpen(false);
      setEditingDocument(null);
    } catch (err) {
      console.error(err);
      setError("Failed to update document version");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSetDocumentLive = async (doc: LegalDocument) => {
    if (!user) return;
    try {
      setUpdatingLiveDocumentId(doc.documentId);
      await LegalService.setDocumentLive(doc.documentId, user.email);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      setError("Failed to set live version");
    } finally {
      setUpdatingLiveDocumentId("");
    }
  };

  const handleStartAnotherGeneration = () => {
    setGenerationFlowState("idle");
    setGenerationProgress({ total: 0, completed: 0, currentType: "" });
    setLastGeneratedTypes([]);
    handleOpenDialog();
  };

  const getLiveLegalPageUrl = (doc: LegalDocument): string => {
    const appParam = encodeURIComponent(doc.appId);
    const path = (() => {
      switch (doc.type) {
        case "privacy":
          return "/privacy";
        case "tos":
          return "/terms";
        case "eula":
          return "/eula";
        case "cookie":
          return "/cookie";
        case "dpa":
          return "/dpa";
        default:
          return "/terms";
      }
    })();
    return `${PUBLIC_WEB_APP_ORIGIN}${path}?app=${appParam}`;
  };

  const handleCopyLiveLink = async (doc: LegalDocument) => {
    try {
      await navigator.clipboard.writeText(getLiveLegalPageUrl(doc));
      setCopiedLinkDocumentId(doc.documentId);
      toast.success("Live legal page link copied.");
      window.setTimeout(() => {
        setCopiedLinkDocumentId((current) =>
          current === doc.documentId ? "" : current,
        );
      }, 1400);
    } catch (copyError) {
      console.error(copyError);
      toast.error("Failed to copy link.");
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          <GavelIcon
            sx={{ mr: 2, verticalAlign: "middle", fontSize: "2rem" }}
          />
          Legal Documents
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Generate and manage legal documents for your apps
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, minWidth: 1200, maxWidth: "100%" }}>
        <CardContent>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 2,
              backgroundColor: "action.hover",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 0.75,
            }}
          >
            <Button
              onClick={() => setMainTab(0)}
              variant={mainTab === 0 ? "contained" : "text"}
              color={mainTab === 0 ? "primary" : "inherit"}
              sx={{
                borderRadius: 1.5,
                py: 1.1,
                textTransform: "none",
                fontWeight: 700,
                justifyContent: "center",
              }}
            >
              Generate
            </Button>
            <Button
              onClick={() => setMainTab(1)}
              variant={mainTab === 1 ? "contained" : "text"}
              color={mainTab === 1 ? "primary" : "inherit"}
              sx={{
                borderRadius: 1.5,
                py: 1.1,
                textTransform: "none",
                fontWeight: 700,
                justifyContent: "center",
              }}
            >
              Reports
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 1.25, display: "block" }}>
            {mainTab === 0
              ? "Create a new privacy policy or terms document."
              : "Browse, manage, and download existing legal document versions."}
          </Typography>
        </CardContent>
      </Card>

      {mainTab === 0 && (
        <>
          {generationFlowState === "in_progress" ? (
            <Card sx={{ mb: 3, width: "100%" }}>
              <CardContent>
                <Box sx={{ py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Creating Legal Documents
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                    We are generating your selected legal documents. This can take a minute.
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      generationProgress.total > 0
                        ? (generationProgress.completed / generationProgress.total) * 100
                        : 0
                    }
                    sx={{ height: 10, borderRadius: 999, mb: 2 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {generationProgress.completed}/{generationProgress.total} completed
                  </Typography>
                  {generationProgress.currentType && (
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                      Currently generating: {generationProgress.currentType}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : generationFlowState === "success" ? (
            <Card sx={{ mb: 3, width: "100%" }}>
              <CardContent>
                <Box sx={{ py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Legal Documents Created Successfully
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                    Generated: {lastGeneratedTypes.join(", ")}.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Button variant="contained" onClick={() => setMainTab(1)}>
                      View Legal Reports
                    </Button>
                    <Button variant="outlined" onClick={handleStartAnotherGeneration}>
                      Generate More
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mb: 3, width: "100%" }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <FormControl fullWidth>
                      <InputLabel>Select registered app</InputLabel>
                      <Select
                        value={selectedApp?.appId || ""}
                        label="Select registered app"
                        onChange={(e) => {
                          const app = apps.find((a) => a.appId === e.target.value);
                          setSelectedApp(app || null);
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a registered app</em>
                        </MenuItem>
                        {apps.map((app) => (
                          <MenuItem key={app.appId} value={app.appId}>
                            {app.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenDialog}
                      disabled={!selectedApp || generating}
                      fullWidth
                      sx={{ minHeight: 56, whiteSpace: "nowrap" }}
                    >
                      Generate Legal Document
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {mainTab === 1 && (
        <>
          <Card sx={{ mb: 3, width: "100%" }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>Select registered app</InputLabel>
                    <Select
                      value={selectedApp?.appId || ""}
                      label="Select registered app"
                      onChange={(e) => {
                        const app = apps.find((a) => a.appId === e.target.value);
                        setSelectedApp(app || null);
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a registered app</em>
                      </MenuItem>
                      {apps.map((app) => (
                        <MenuItem key={app.appId} value={app.appId}>
                          {app.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {loading && managedDocuments.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : !selectedApp ? (
            <Paper sx={{ p: 6, textAlign: "center", backgroundColor: "action.hover" }}>
              <GavelIcon sx={{ fontSize: "4rem", color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No app selected
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Select a registered app to view legal document reports
              </Typography>
            </Paper>
          ) : groupedDocumentsByDate.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: "center", backgroundColor: "action.hover" }}>
              <DescriptionIcon sx={{ fontSize: "4rem", color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No legal documents reports yet
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Switch to "Generate New Legal Document" to create the first one
              </Typography>
            </Paper>
          ) : (
            <Box>
              {groupedDocumentsByDate.map((group, index) => (
                <Accordion key={group.dateKey} defaultExpanded={index === 0} sx={{ mb: 1.5 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.label} ({group.docs.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {group.docs.map((doc) => (
                        <Grid item xs={12} key={doc.documentId}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {LegalService.getDocumentTypeName(doc.type)}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Chip
                                    label={`v${doc.versionNumber || doc.version}`}
                                    size="small"
                                    color="primary"
                                  />
                                  {doc.isLive && (
                                    <Chip
                                      label="Live"
                                      size="small"
                                      color="success"
                                      icon={<CheckCircleIcon />}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                                Last updated: {doc.lastUpdated.toLocaleDateString()} | Effective: {doc.effectiveDate.toLocaleDateString()}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewDocument(doc)}>
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<OpenInNewIcon />}
                                  onClick={() =>
                                    window.open(getLiveLegalPageUrl(doc), "_blank", "noopener,noreferrer")
                                  }
                                >
                                  View Live Page
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={
                                    copiedLinkDocumentId === doc.documentId ? (
                                      <CheckIcon />
                                    ) : (
                                      <ContentCopyIcon />
                                    )
                                  }
                                  color={copiedLinkDocumentId === doc.documentId ? "success" : "inherit"}
                                  onClick={() => handleCopyLiveLink(doc)}
                                >
                                  {copiedLinkDocumentId === doc.documentId ? "Copied" : "Copy Link"}
                                </Button>
                                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditDocument(doc)}>
                                  Edit
                                </Button>
                                <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => handleDownloadPdf(doc)}>
                                  Download PDF
                                </Button>
                                <Button size="small" startIcon={<TextSnippetIcon />} onClick={() => handleDownloadPlainText(doc)}>
                                  Download TXT
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleSetDocumentLive(doc)}
                                  disabled={doc.isLive || updatingLiveDocumentId === doc.documentId}
                                >
                                  {doc.isLive
                                    ? "Live"
                                    : updatingLiveDocumentId === doc.documentId
                                    ? "Updating..."
                                    : "Set Live"}
                                </Button>
                                <IconButton size="small" onClick={() => handleDeleteClick(doc)} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Generate Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Generate Legal Document
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Pre-filled from your selected app. Review details before generating.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                    Document Setup
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Document Types
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          minHeight: 140,
                          backgroundColor: "action.hover",
                        }}
                      >
                        {GENERATABLE_DOC_TYPES.map((type) => (
                          <FormControlLabel
                            key={type}
                            control={
                              <Checkbox
                                checked={selectedGenerationTypes.includes(type)}
                                onChange={() => toggleGenerationType(type)}
                              />
                            }
                            label={getLegalTypeLabel(type)}
                          />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Generation Model</InputLabel>
                        <Select
                          value={formData.generationModel || ""}
                          label="Generation Model"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              generationModel: String(e.target.value),
                            })
                          }
                        >
                          {LEGAL_TEXT_MODELS.map((modelName) => (
                            <MenuItem key={modelName} value={modelName}>
                              {modelName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                    Company & Contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company Name (App Name)"
                        value={formData.companyName}
                        InputProps={{ readOnly: true }}
                        helperText="Auto-filled from selected app."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Contact Email"
                        value={formData.contactEmail}
                        onChange={(e) =>
                          setFormData({ ...formData, contactEmail: e.target.value })
                        }
                        helperText="Defaults to violesoftware@gmail.com."
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company Address (Optional)"
                        value={formData.companyAddress}
                        onChange={(e) =>
                          setFormData({ ...formData, companyAddress: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Website (Optional)"
                        value={formData.website}
                        onChange={(e) =>
                          setFormData({ ...formData, website: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Jurisdiction</InputLabel>
                        <Select
                          value={formData.jurisdiction}
                          label="Jurisdiction"
                          onChange={(e) =>
                            setFormData({ ...formData, jurisdiction: e.target.value })
                          }
                        >
                          <MenuItem value="US">United States</MenuItem>
                          <MenuItem value="EU">European Union</MenuItem>
                          <MenuItem value="UK">United Kingdom</MenuItem>
                          <MenuItem value="CA">Canada</MenuItem>
                          <MenuItem value="AU">Australia</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>Data Collection</Divider>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Personal Information Collected
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Common personal info items</InputLabel>
                    <Select
                      multiple
                      value={formData.dataCollection.personalInfo}
                      label="Common personal info items"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataCollection: {
                            ...formData.dataCollection,
                            personalInfo: e.target.value as string[],
                          },
                        })
                      }
                      renderValue={(selected) =>
                        (selected as string[]).length > 0
                          ? (selected as string[]).join(", ")
                          : "None selected"
                      }
                    >
                      {COMMON_PERSONAL_INFO_ITEMS.map((item) => (
                        <MenuItem key={item} value={item}>
                          <Checkbox
                            checked={formData.dataCollection.personalInfo.includes(
                              item,
                            )}
                          />
                          <Typography variant="body2">{item}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="e.g., Email address"
                      value={personalInfoInput}
                      onChange={(e) => setPersonalInfoInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && addPersonalInfo()}
                      fullWidth
                    />
                    <Button onClick={addPersonalInfo}>Add</Button>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {formData.dataCollection.personalInfo.map((info, index) => (
                      <Chip
                        key={index}
                        label={info}
                        onDelete={() => removePersonalInfo(index)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Third-Party Services
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="e.g., Google Analytics"
                      value={thirdPartyInput}
                      onChange={(e) => setThirdPartyInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && addThirdParty()}
                      fullWidth
                    />
                    <Button onClick={addThirdParty}>Add</Button>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {formData.dataCollection.thirdPartyServices.map(
                      (service, index) => (
                        <Chip
                          key={index}
                          label={service}
                          onDelete={() => removeThirdParty(index)}
                          size="small"
                        />
                      ),
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dataCollection.cookies}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataCollection: {
                            ...formData.dataCollection,
                            cookies: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Uses Cookies"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dataCollection.analytics}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataCollection: {
                            ...formData.dataCollection,
                            analytics: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Uses Analytics"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dataCollection.advertising}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataCollection: {
                            ...formData.dataCollection,
                            advertising: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Uses Advertising"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>App Features</Divider>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  All features are pre-selected by default.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.features.userAccounts}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            userAccounts: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="User Accounts"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.features.inAppPurchases}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            inAppPurchases: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="In-App Purchases"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.features.userGeneratedContent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            userGeneratedContent: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="User Generated Content"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.features.location}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            location: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Location Services"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.features.pushNotifications}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            pushNotifications: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Push Notifications"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={generating || selectedGenerationTypes.length === 0}
          >
            {generating
              ? <CircularProgress size={24} />
              : `Generate ${selectedGenerationTypes.length || ""} Document${
                  selectedGenerationTypes.length === 1 ? "" : "s"
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog
        open={viewDocumentOpen}
        onClose={() => setViewDocumentOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {viewingDocument &&
            LegalService.getDocumentTypeName(viewingDocument.type)}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={viewTab}
            onChange={(e, v) => setViewTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="Preview" />
            <Tab label="Markdown" />
          </Tabs>
          {viewTab === 0 && viewingDocument && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "action.hover",
                borderRadius: 1,
                maxHeight: "60vh",
                overflow: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: viewingDocument.htmlContent }}
            />
          )}
          {viewTab === 1 && viewingDocument && (
            <TextField
              multiline
              fullWidth
              rows={20}
              value={viewingDocument.content}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDocumentOpen(false)}>Close</Button>
          {viewingDocument && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handleDownloadPdf(viewingDocument)}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<TextSnippetIcon />}
                onClick={() => handleDownloadPlainText(viewingDocument)}
              >
                TXT
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Version Dialog */}
      <Dialog
        open={editDocumentOpen}
        onClose={() => setEditDocumentOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Version{" "}
          {editingDocument
            ? `v${editingDocument.versionNumber || editingDocument.version}`
            : ""}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            rows={22}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDocumentOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEditedDocument}
            variant="contained"
            disabled={savingEdit}
          >
            {savingEdit ? <CircularProgress size={24} /> : "Save Version"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this{" "}
            <strong>
              {docToDelete &&
                LegalService.getDocumentTypeName(docToDelete.type)}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Legal;
